/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Exocet Scroll Motion — GSAP ScrollTrigger sitewide
 */
(function () {
  "use strict";

  if (document.documentElement.dataset.scrollMotion === "off") return;

  const BOUND = "data-scroll-motion-bound";
  const VISIBLE = "scroll-motion-visible";

  const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const PRESETS = {
    fade: {
      from: { opacity: 0 },
      to: { opacity: 1, duration: 1.1, ease: "power3.out" },
    },
    slide: {
      from: { opacity: 0, y: 32 },
      to: { opacity: 1, y: 0, duration: 1.2, ease: "expo.out" },
    },
    scale: {
      from: { opacity: 0, y: 22, scale: 0.985 },
      to: { opacity: 1, y: 0, scale: 1, duration: 0.62, ease: "power3.out" },
    },
    // Cinematic media reveal: image stays opaque and wipes open from the bottom.
    // Downgraded to fade on mobile (clip-path can jank on small Safari).
    clip: {
      from: { clipPath: "inset(0 0 100% 0)", opacity: 1 },
      to: { clipPath: "inset(0 0 0% 0)", duration: 1.1, ease: "power4.inOut" },
    },
  };

  const TRIGGER = {
    start: "top 90%",
    once: true,
  };

  // Handcrafted text reveals (real SplitText). Brand easing vocabulary mirrors the
  // --animation-* cubic-beziers: expo.out / power3.out (no CustomEase plugin needed).
  // chars is intentionally restricted to short labels — never body copy (tween count / a11y).
  const SPLIT_DEFAULTS = {
    lines: {
      from: { yPercent: 110, opacity: 0 },
      to: { yPercent: 0, opacity: 1, duration: 0.9, ease: "expo.out" },
      stagger: 0.08,
    },
    words: { from: { y: 24, opacity: 0 }, to: { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" }, stagger: 0.04 },
    chars: { from: { y: 16, opacity: 0 }, to: { y: 0, opacity: 1, duration: 0.4, ease: "power3.out" }, stagger: 0.02 },
  };

  // Resolve before splitting so line wrapping uses final font metrics (the "no flux" guarantee).
  // Only the split path waits on this; block reveals stay synchronous. 900ms matches the
  // inline <head> guard timeout so text is never stuck hidden if fonts never settle.
  function fontsReady(cb) {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      cb();
    };
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(go);
    } else {
      go();
    }
    window.setTimeout(go, 900);
  }

  // The home hero owns its own entrance + manual typewriter — never split inside it.
  function isHeroOwned(el) {
    return (
      el.hasAttribute("data-hero-typewriter") || !!el.closest("[data-hero-typewriter]") || !!el.closest(".home-hero")
    );
  }

  function pluginsReady() {
    return typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined";
  }

  function isBound(el) {
    return el && el.hasAttribute(BOUND);
  }

  function markBound(el) {
    el.setAttribute(BOUND, "");
  }

  function markVisible(el) {
    el.classList.add(VISIBLE);
  }

  function cascadeDelay(el) {
    const inline = el.style.getPropertyValue("--animation-order").trim();
    const fromData = el.dataset.cascadeOrder;
    const order = inline !== "" ? inline : fromData !== undefined && fromData !== "" ? fromData : "0";
    const parsed = parseFloat(order);
    return Number.isFinite(parsed) ? parsed * 0.1 : 0;
  }

  function killInRoot(root) {
    if (!pluginsReady()) return;
    ScrollTrigger.getAll().forEach((st) => {
      const trigger = st.trigger;
      if (trigger && root.contains(trigger)) st.kill();
    });
  }

  function revealElement(el, presetName, extraDelay) {
    if (!el || isBound(el)) return null;

    let preset = PRESETS[presetName] || PRESETS.slide;
    if (presetName === "clip" && window.matchMedia("(max-width: 749px)").matches) {
      preset = PRESETS.fade;
    }
    markBound(el);
    gsap.set(el, { ...preset.from, visibility: "visible" });

    return gsap.to(el, {
      ...preset.to,
      delay: cascadeDelay(el) + (extraDelay || 0),
      onComplete: () => markVisible(el),
      scrollTrigger: {
        trigger: el,
        ...TRIGGER,
      },
    });
  }

  function initLegacyReveals(root) {
    root.querySelectorAll(".scroll-trigger.animate--fade-in").forEach((el) => {
      revealElement(el, "fade");
    });

    root.querySelectorAll(".scroll-trigger.animate--slide-in").forEach((el) => {
      revealElement(el, "slide");
    });
  }

  function initDataReveals(root) {
    root.querySelectorAll("[data-scroll-reveal]").forEach((el) => {
      if (isBound(el)) return;
      revealElement(el, el.dataset.scrollReveal || "slide");
    });
  }

  function initStaggerGroups(root) {
    root.querySelectorAll("[data-scroll-stagger]").forEach((group) => {
      if (isBound(group)) return;

      const children = group.querySelectorAll("[data-scroll-stagger-item]");
      if (!children.length) return;

      const presetName = group.dataset.scrollReveal || "scale";
      const preset = PRESETS[presetName] || PRESETS.scale;
      const stagger = parseFloat(group.dataset.scrollStagger) || 0.1;

      markBound(group);
      children.forEach(markBound);
      gsap.set(children, preset.from);

      gsap.to(children, {
        ...preset.to,
        stagger,
        onComplete: () => children.forEach(markVisible),
        scrollTrigger: {
          trigger: group,
          start: "top 88%",
          once: true,
        },
      });
    });
  }

  function initProductsInfiniteReveal(root) {
    root.querySelectorAll(".products-infinite-slider").forEach((section) => {
      if (isBound(section)) return;

      const headerItems = section.querySelectorAll(".products-infinite-slider__header > *");
      const cards = section.querySelectorAll(".product-card");
      const mobile = window.matchMedia("(max-width: 749px)").matches;
      if (!headerItems.length && !cards.length) return;

      markBound(section);
      headerItems.forEach(markBound);
      cards.forEach(markBound);

      const preset = PRESETS.scale;
      const cardPreset = mobile
        ? { from: { opacity: 0, y: 22 }, to: { opacity: 1, y: 0, duration: 0.42, ease: "power3.out" } }
        : preset;

      if (!mobile) {
        if (!headerItems.length) {
          markVisible(section);
          cards.forEach(markVisible);
          return;
        }

        gsap.set(headerItems, preset.from);
        gsap.to(headerItems, {
          ...preset.to,
          stagger: 0.12,
          onComplete: () => {
            markVisible(section);
            headerItems.forEach(markVisible);
            cards.forEach(markVisible);
          },
          scrollTrigger: {
            trigger: section,
            start: "top 88%",
            once: true,
          },
        });
        return;
      }

      if (headerItems.length) gsap.set(headerItems, preset.from);
      if (cards.length) gsap.set(cards, cardPreset.from);
      if (cards.length) {
        gsap.set(cards[0], { opacity: 1, y: 0, scale: 1 });
        markVisible(cards[0]);
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 88%",
          once: true,
        },
        onComplete: () => {
          markVisible(section);
          headerItems.forEach(markVisible);
          cards.forEach(markVisible);
        },
      });

      headerItems.forEach((item, index) => {
        tl.to(item, { ...preset.to, duration: 0.45 }, index * 0.06);
      });
      cards.forEach((card, index) => {
        if (index === 0) return;
        tl.to(card, { ...cardPreset.to, duration: 0.42 }, 0.08 + (index - 1) * 0.05);
      });
    });
  }

  function initProductGridReveal(root) {
    root.querySelectorAll(".home-products-grid").forEach((section) => {
      if (isBound(section)) return;

      const headerItems = section.querySelectorAll(".home-products-grid__header > *");
      const cards = section.querySelectorAll(".product-card");
      if (!headerItems.length && !cards.length) return;

      markBound(section);
      headerItems.forEach(markBound);
      cards.forEach(markBound);

      const preset = PRESETS.scale;
      const mobile = window.matchMedia("(max-width: 749px)").matches;
      const cardPreset = mobile
        ? { from: { opacity: 0, y: 22 }, to: { opacity: 1, y: 0, duration: 0.42, ease: "power3.out" } }
        : preset;

      if (mobile) {
        if (headerItems.length) gsap.set(headerItems, preset.from);
        if (cards.length) gsap.set(cards, cardPreset.from);

        if (cards.length) {
          gsap.set(cards[0], { opacity: 1, y: 0, scale: 1 });
          markVisible(cards[0]);
        }

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: "top 88%",
            once: true,
          },
          onComplete: () => {
            markVisible(section);
            headerItems.forEach(markVisible);
            cards.forEach(markVisible);
          },
        });

        headerItems.forEach((item, index) => {
          tl.to(item, { ...preset.to, duration: 0.45 }, index * 0.06);
        });

        cards.forEach((card, index) => {
          if (index === 0) return;
          tl.to(card, { ...cardPreset.to, duration: 0.42 }, 0.08 + (index - 1) * 0.05);
        });
        return;
      }

      gsap.set([...headerItems, ...cards], preset.from);

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 88%",
          once: true,
        },
        onComplete: () => {
          markVisible(section);
          headerItems.forEach(markVisible);
          cards.forEach(markVisible);
        },
      });

      headerItems.forEach((item, index) => {
        tl.to(item, { ...preset.to, duration: 0.58 }, index * 0.1);
      });

      cards.forEach((card, index) => {
        tl.to(card, { ...preset.to, duration: 0.62 }, 0.28 + index * 0.11);
      });
    });
  }

  function initZoomIn(root) {
    root.querySelectorAll(".animate--zoom-in").forEach((wrapper) => {
      if (isBound(wrapper)) return;

      const target = wrapper.querySelector("img, .svg-wrapper");
      if (!target) return;

      markBound(wrapper);
      markBound(target);

      gsap.fromTo(
        target,
        { scale: 1 },
        {
          scale: 1.2,
          ease: "none",
          scrollTrigger: {
            trigger: wrapper,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );
    });
  }

  function initSplitReveals(root) {
    const els = root.querySelectorAll("[data-split-reveal]");
    if (!els.length) return;

    // Graceful degrade: GSAP core is guaranteed here (boot returns early otherwise), but if the
    // SplitText plugin is missing, reveal as a block slide so text is never left hidden.
    if (typeof SplitText === "undefined") {
      els.forEach((el) => {
        if (isBound(el) || isHeroOwned(el)) return;
        revealElement(el, "slide");
      });
      return;
    }

    els.forEach((el) => {
      if (isBound(el) || isHeroOwned(el)) return;
      markBound(el);

      const type = SPLIT_DEFAULTS[el.dataset.splitReveal] ? el.dataset.splitReveal : "lines";
      const spec = SPLIT_DEFAULTS[type];
      const stagger = parseFloat(el.dataset.splitStagger) || spec.stagger;
      const ease = el.dataset.splitEase || spec.to.ease;
      const delay = cascadeDelay(el);
      const useMask = el.hasAttribute("data-split-mask") && type === "lines";

      // autoSplit re-splits + re-runs onSplit on resize / font load (3.13) — no manual resize code.
      el._splitInstance = SplitText.create(el, {
        type,
        mask: useMask ? "lines" : false,
        aria: "auto",
        autoSplit: true,
        onSplit(self) {
          const parts = self[type];
          // Lift the CSS container pre-hide; the line/word/char parts carry the hidden state.
          gsap.set(el, { opacity: 1 });

          if (!parts || !parts.length) {
            markVisible(el);
            return;
          }

          // Re-split after the reveal already played (resize/font swap): settle to the final
          // state without re-animating — perfectly calculated, no flicker on resize.
          if (el.hasAttribute("data-split-done")) {
            gsap.set(parts, { yPercent: 0, y: 0, opacity: 1, clearProps: "willChange" });
            return;
          }

          gsap.set(parts, { ...spec.from, willChange: "transform" });
          return gsap.to(parts, {
            ...spec.to,
            ease,
            stagger,
            delay,
            onComplete() {
              el.setAttribute("data-split-done", "");
              gsap.set(parts, { clearProps: "willChange" });
              markVisible(el);
            },
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              once: true,
              invalidateOnRefresh: true,
            },
          });
        },
      });
    });
  }

  function revertSplits(root) {
    root.querySelectorAll("[data-split-reveal]").forEach((el) => {
      if (el._splitInstance && typeof el._splitInstance.revert === "function") {
        try {
          el._splitInstance.revert();
        } catch (e) {
          /* no-op */
        }
      }
      el._splitInstance = null;
      el.removeAttribute("data-split-done");
      el.removeAttribute(BOUND);
    });
  }

  function designModeReveal(root) {
    root.querySelectorAll(".scroll-trigger").forEach((el) => {
      el.classList.remove("scroll-trigger--offscreen");
      el.classList.add("scroll-trigger--design-mode");
    });

    const targets = root.querySelectorAll(
      ".scroll-trigger, [data-scroll-reveal], [data-scroll-stagger-item], .home-products-grid .product-card",
    );
    gsap.set(targets, { clearProps: "all", opacity: 1, y: 0, scale: 1 });
    targets.forEach(markVisible);

    // Split text: revert the SplitText instance and show the source element plainly.
    revertSplits(root);
    root.querySelectorAll("[data-split-reveal]").forEach((el) => {
      gsap.set(el, { clearProps: "all", opacity: 1 });
      markVisible(el);
    });
  }

  // --- Fail-safe reveal -----------------------------------------------------
  // GSAP ScrollTrigger reveals (start "top 88/90%", once: true) can be skipped when the
  // user fast-scrolls, jumps via an in-page anchor, or restores a mid-page scroll position
  // (bfcache / reload). The target then stays stuck at opacity:0 — a blank/black section.
  // failSafeReveal force-shows any BOUND reveal element that is already in/above the viewport
  // yet still hidden. It runs only AFTER entrance animations have had time to play (delayed)
  // and on navigation events, so it never pre-empts a normal on-scroll reveal: elements still
  // below the fold keep their hidden state and animate in as usual. Scrub/parallax targets
  // (zoom-in) keep opacity:1 and are skipped by the isStuckHidden() opacity check.
  function splitPartsHidden(el) {
    const inst = el._splitInstance;
    if (!inst) return false;
    const parts = inst.lines || inst.words || inst.chars;
    return !!(parts && parts.length && parseFloat(getComputedStyle(parts[0]).opacity) < 0.01);
  }

  function isStuckHidden(el) {
    return parseFloat(getComputedStyle(el).opacity) < 0.01 || splitPartsHidden(el);
  }

  function forceShow(el) {
    gsap.set(el, { clearProps: "transform,clipPath", opacity: 1 });
    el.style.visibility = "visible";
    const inst = el._splitInstance;
    if (inst) {
      ["lines", "words", "chars"].forEach((t) => {
        if (inst[t] && inst[t].length) gsap.set(inst[t], { yPercent: 0, y: 0, opacity: 1 });
      });
    }
    markVisible(el);
  }

  function failSafeReveal() {
    if (!pluginsReady()) return;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    document.querySelectorAll("[" + BOUND + "]").forEach((el) => {
      if (el.classList.contains(VISIBLE) || !isStuckHidden(el)) return;
      const rect = el.getBoundingClientRect();
      // Already in or above the viewport (small margin) but still hidden → reveal now.
      if (rect.bottom > 0 && rect.top < vh * 0.98) forceShow(el);
    });
  }

  function boot(root, designMode) {
    if (!pluginsReady()) {
      // GSAP / ScrollTrigger failed to load. The FOUC guard in scroll-motion.css
      // hides reveal targets under html[data-scroll-motion="on"]. Add the reduced
      // class so those elements become visible without waiting for animations that
      // will never run. The "off" path (line 8 guard) never sets data-scroll-motion
      // to "on", so those pages are never hidden and do not need this class.
      document.documentElement.classList.add("scroll-motion-reduced");
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    if (reducedMotion()) {
      document.documentElement.classList.add("scroll-motion-reduced");
      return;
    }

    if (designMode) {
      designModeReveal(root);
      ScrollTrigger.refresh();
      return;
    }

    initLegacyReveals(root);
    initDataReveals(root);
    initStaggerGroups(root);
    initProductGridReveal(root);
    initProductsInfiniteReveal(root);
    initZoomIn(root);

    document.documentElement.classList.add("scroll-motion-ready");
    ScrollTrigger.refresh();

    // Split text waits for fonts so line wrapping is final before reveal (no flux).
    fontsReady(function () {
      initSplitReveals(root);
      if (pluginsReady()) ScrollTrigger.refresh();
    });

    // Safety net: rescue any reveal left stuck hidden once entrance animations have settled
    // (fast scroll / anchor jump / restored scroll position). See failSafeReveal().
    window.setTimeout(failSafeReveal, 1500);
  }

  function onReady() {
    boot(document, false);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }

  // Re-run the safety net on navigations that can land mid-page past a reveal trigger:
  // full load (after images/layout settle), bfcache restore (back/forward), and in-page
  // anchor jumps. Cheap no-op when motion is off or plugins never loaded.
  window.addEventListener("load", function () {
    window.setTimeout(failSafeReveal, 250);
  });
  window.addEventListener("pageshow", function (event) {
    if (event.persisted) failSafeReveal();
  });
  window.addEventListener("hashchange", function () {
    window.setTimeout(failSafeReveal, 60);
  });

  if (typeof Shopify !== "undefined" && Shopify.designMode) {
    document.addEventListener("shopify:section:load", (event) => {
      killInRoot(event.target);
      boot(event.target, false);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      killInRoot(event.target);
      revertSplits(event.target);
    });

    document.addEventListener("shopify:section:reorder", () => {
      if (pluginsReady()) ScrollTrigger.refresh();
    });

    document.addEventListener("shopify:section:select", (event) => {
      boot(event.target, true);
    });
  }

  window.ExocetScrollMotion = {
    refresh() {
      if (pluginsReady()) ScrollTrigger.refresh();
    },
    boot,
  };

  if (window.ExocetMotion) {
    ExocetMotion.register(window.ExocetScrollMotion);
  }
})();
